// Text formatting utilities for blog content

/**
 * Formats text to preserve line breaks and handle various text formatting
 * @param {string} text - The text to format
 * @returns {string} - Formatted text with proper line breaks
 */
export const formatTextWithLineBreaks = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Replace double line breaks with paragraph breaks
  let formatted = text
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Wrap in paragraph tags if not already wrapped
  if (!formatted.startsWith('<p>')) {
    formatted = `<p>${formatted}</p>`;
  }

  // Clean up any empty paragraphs
  formatted = formatted.replace(/<p><\/p>/g, '');
  formatted = formatted.replace(/<p><br><\/p>/g, '');

  return formatted;
};

/**
 * Formats text for display in excerpts (removes HTML tags and limits length)
 * @param {string} text - The text to format
 * @param {number} maxLength - Maximum length for excerpt (default: 150)
 * @returns {string} - Formatted excerpt
 */
export const formatExcerpt = (text, maxLength = 150) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Remove HTML tags and line breaks for excerpt
  let excerpt = text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/\n+/g, ' ') // Replace line breaks with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();

  // Truncate if too long
  if (excerpt.length > maxLength) {
    excerpt = excerpt.substring(0, maxLength).trim();
    // Find last complete word
    const lastSpaceIndex = excerpt.lastIndexOf(' ');
    if (lastSpaceIndex > 0) {
      excerpt = excerpt.substring(0, lastSpaceIndex);
    }
    excerpt += '...';
  }

  return excerpt;
};

/**
 * Formats section content with proper line breaks and numbering
 * @param {string} explanation - The section explanation text
 * @returns {string} - Formatted HTML content
 */
export const formatSectionContent = (explanation) => {
  if (!explanation || typeof explanation !== 'string') {
    return '';
  }

  let formatted = explanation;

  // Handle numbered lists (1., 2., etc.)
  formatted = formatted.replace(/(\d+\.\s)/g, '<br>$1');
  
  // Handle bullet points (•, -, *)
  formatted = formatted.replace(/([•\-*]\s)/g, '<br>$1');
  
  // Replace line breaks with HTML breaks
  formatted = formatted.replace(/\n/g, '<br>');
  
  // Clean up multiple breaks
  formatted = formatted.replace(/(<br>){3,}/g, '<br><br>');
  
  // Remove leading/trailing breaks
  formatted = formatted.replace(/^(<br>)+|(<br>)+$/g, '');

  return formatted;
};

/**
 * Formats blog content for full display (combines sections)
 * @param {Array} sections - Array of section objects
 * @returns {string} - Formatted HTML content
 */
export const formatBlogContent = (sections) => {
  if (!sections || !Array.isArray(sections) || sections.length === 0) {
    return '';
  }

  return sections.map(section => {
    const header = section.header ? `<h2>${section.header}</h2>` : '';
    const explanation = section.explanation ? formatSectionContent(section.explanation) : '';
    return `${header}<p>${explanation}</p>`;
  }).join('');
};

export default {
  formatTextWithLineBreaks,
  formatExcerpt,
  formatSectionContent,
  formatBlogContent
};
